import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronLeft, Home, Calculator, RotateCcw } from 'lucide-react';
import Layout from '../../../../components/Layout';
import { Button } from '../../../../components/ui/Button';

const initialValues = { r1: '', r2: '', result: '' };

/**
 * RFC r1 r2 to R1+R2 Converter
 * Converts ring circuit end-to-end r1 (Line) and r2 (cpc) measurements to R1+R2.
 * Formula: R1+R2 = (r1 + r2) / 4 for a ring circuit (parallel paths).
 * Simplified: For direct sum: R1+R2 = r1 + r2 (varies by application).
 * BS 7671 ring final circuit: R1+R2 = (r1 + r2) / 4 (at any point on the ring).
 */
function InputField({ label, placeholder, value, onChange, readOnly = false, unit = 'Ω' }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 overflow-hidden">
      <label className="flex shrink-0 items-center px-3 py-2.5 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium min-w-[5rem]">
        {label}
      </label>
      <input
        type="number"
        inputMode="decimal"
        min="0"
        step="0.01"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        readOnly={readOnly}
        className={`flex-1 min-w-0 px-3 py-2.5 text-sm border-0 focus:outline-none focus:ring-0 bg-transparent text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 ${
          readOnly ? 'cursor-default bg-gray-100 dark:bg-gray-600/50' : ''
        }`}
        aria-label={label}
      />
      <span className="flex shrink-0 items-center px-3 py-2.5 text-sm text-gray-600 dark:text-gray-400 font-medium">
        {unit}
      </span>
    </div>
  );
}

export default function RFCR1R2Converter() {
  const location = useLocation();
  const isMobile = location.pathname.startsWith('/tenant/mobile');

  const [values, setValues] = useState(initialValues);

  const backPath = isMobile ? '/tenant/mobile/calculators' : '/tenant/tools/calculators';

  const updateValue = (field, val) => {
    setValues((p) => ({ ...p, [field]: val, result: '' }));
  };

  const handleCalculate = () => {
    const r1 = parseFloat(values.r1);
    const r2 = parseFloat(values.r2);

    if (r1 == null || r2 == null || isNaN(r1) || isNaN(r2) || r1 < 0 || r2 < 0) return;

    // R1+R2 = (r1 + r2) / 4 for ring final circuit (parallel paths at any point)
    const result = (r1 + r2) / 4;
    setValues((p) => ({ ...p, result: result.toFixed(4) }));
  };

  const handleReset = () => {
    setValues({ ...initialValues });
  };

  const r1 = parseFloat(values.r1);
  const r2 = parseFloat(values.r2);
  const canCalculate =
    r1 != null && r2 != null && !isNaN(r1) && !isNaN(r2) && r1 >= 0 && r2 >= 0 &&
    (values.r1 !== '' || values.r2 !== '');

  const content = (
    <div className="max-w-2xl mx-auto">
      {/* Header bar - green, matching screenshot */}
      <div className="flex items-center justify-between bg-green-600 dark:bg-green-700 px-4 py-3 rounded-t-lg -mx-4 -mt-4 mb-0 sm:rounded-t-none sm:mx-0 sm:mt-0 sm:-mx-0 sm:-mt-0 sm:rounded-lg">
        <Link
          to={backPath}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white hover:bg-white/20 transition-colors"
          aria-label="Back"
        >
          <ChevronLeft className="h-5 w-5" />
        </Link>
        <h1 className="text-lg font-semibold text-white">r1 r2 to R1+R2 Converter</h1>
        <Link
          to={isMobile ? '/tenant/mobile' : '/tenant/dashboard'}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white hover:bg-white/20 transition-colors"
          aria-label="Home"
        >
          <Home className="h-5 w-5" />
        </Link>
      </div>

      <p className="text-sm text-gray-600 dark:text-gray-400 mt-4 mb-4">
        Enter your ring circuit r1 (Line) and r2 (cpc) end to end values to convert to R1+R2
      </p>

      <div className={`space-y-4 ${isMobile ? 'pb-8' : ''}`}>
        <InputField
          label="r1"
          placeholder="0"
          value={values.r1}
          onChange={(val) => updateValue('r1', val)}
        />
        <InputField
          label="r2"
          placeholder="0"
          value={values.r2}
          onChange={(val) => updateValue('r2', val)}
        />
        <InputField
          label="R1+R2"
          placeholder="Result"
          value={values.result}
          onChange={() => {}}
          readOnly
        />

        {/* Action buttons - Calculate (green), Reset (red) */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={handleCalculate}
            disabled={!canCalculate}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Calculator className="h-4 w-4" />
            Calculate
          </Button>
          <Button
            onClick={handleReset}
            variant="destructive"
            className="flex-1 py-3 flex items-center justify-center gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            Reset
          </Button>
        </div>

        {/* Formula reference */}
        <div className="mt-6 space-y-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-4">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Formula: R1+R2 = (r1 + r2) ÷ 4
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            For a ring final circuit, R1+R2 at any point is (r1 + r2) divided by 4, due to parallel paths.
          </p>
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <div className="px-4 py-6 pb-8 min-h-screen bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
        {content}
      </div>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 px-4 sm:px-6 lg:px-8 py-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            RFC r1 r2 to R1+R2 Converter
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Convert ring circuit end-to-end r1 (Line) and r2 (cpc) measurements to R1+R2
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-6">
          {content}
        </div>
      </div>
    </Layout>
  );
}
